import { TemsObject3D } from "@/CommonTypes";
import { MeshInfo, MeshGroup, MeshPointCloud, ResultThermoTextureInfo } from "@/TemsManagerWebInterface";
import { SurfaceMaterial } from "@/Three/ThreeView";
import { ResultPartSide } from "@/TemsManagerWebEnums";
var pointSprite;

new THREE.TextureLoader().load("/ball.png", (tex) => {
    pointSprite = tex;
});

export default class ThreeScene {
    public scene = new THREE.Scene();
    private sceneryObject = new THREE.Group();
    private changeSubscribers: (() => void)[] = [];

    constructor(meshInfos: MeshInfo[], elementCreator?: (s: THREE.Scene) => void) {
        elementCreator(this.scene);
        this.scene.add(new THREE.AmbientLight(0x7F7F7F));
        if (meshInfos) {
            $.each(meshInfos, (meshInfoIdx, meshInfo) => {
                this.sceneryObject.add(
                    this._changeRendering(
                        this.getMeshFromMeshInfo(meshInfo),
                        SurfaceMaterial.Surface)
                );
            });
        }
        this.scene.add(this.sceneryObject);
    }

    public onMeshChange(callback: () => void) {
        this.changeSubscribers.push(callback);
    }

    private fireMeshChange() {
        for (let i = 0; i < this.changeSubscribers.length; i++) {
            this.changeSubscribers[i]();
        }
    }

    public offMeshChange(callback: () => void) {
        let index = this.changeSubscribers.indexOf(callback, 0);
        if (index === -1) {
            throw new Error("Unknown Callback. Unable to unsubscribe");
        }
        this.changeSubscribers.splice(index, 1);
    }

    public changeRenderingMode(newRenderingMode: SurfaceMaterial) {
        this._changeRendering(this.sceneryObject, newRenderingMode);
    }

    public changeSpecificRenderingMode(meshId: string, newRenderingMode: SurfaceMaterial): TemsObject3D {
        var object3D = this.getObject3DFromMeshId(meshId);
        return this._changeRendering(object3D, newRenderingMode);
    }

    public recreateMeshById(meshId: string) {
        var mesh = this.getObject3DFromMeshId(meshId)
        if (!mesh) {
            throw "Can't recreate Mesh by ID: '" + meshId + "' not found";
        }
        this.exchangeObject(mesh, this.getMeshFromMeshInfo(mesh.meshInfo));
    }

    private getMeshFromMeshInfo(meshInfo): TemsObject3D {
        var mesh = null;
        if ("Children" in meshInfo) {
            // we have a group, there is no geometry, just a transform!
            mesh = new THREE.Group();
            for (var i = 0, childrenLen = meshInfo.Children.length; i < childrenLen; i++) {
                mesh.add(this.getMeshFromMeshInfo(meshInfo.Children[i]));
            }
        } else {
            let defaultMaterial: THREE.Material;
            let surfaceMaterial: THREE.Material;
            let wireframeMaterial: THREE.Material;
            let pointCloudMaterial: THREE.PointCloudMaterial;

            pointCloudMaterial = new THREE.PointCloudMaterial({ size: 0.1, sizeAttenuation: true, map: pointSprite, alphaTest: 0.5, vertexColors: THREE.VertexColors });
            if (!("ColorStrategy" in meshInfo)) {
                pointCloudMaterial.vertexColors = false;
            }

            if (meshInfo.Shader) {
                surfaceMaterial = new THREE.ShaderMaterial(this.shaders[meshInfo.Shader]);
            } else {
                surfaceMaterial = new THREE.MeshPhongMaterial({
                    color: meshInfo.Color,
                    opacity: meshInfo.Opacity,
                    transparent: meshInfo.Opacity < 1,
                    side: THREE.DoubleSide,
                    shading: THREE.FlatShading,
                    shininess: 5
                });
            }
            defaultMaterial = surfaceMaterial;

            var geometry;
            if ("SizeX" in meshInfo) { // Cube
                geometry = new THREE.BoxGeometry(meshInfo.SizeX, meshInfo.SizeY, meshInfo.SizeZ);
            } else if ("AngleFrom" in meshInfo) { // Circle
                geometry = new THREE.CircleGeometry(meshInfo.Radius, 12, meshInfo.AngleFrom, meshInfo.AngleLength);
            } else if ("Radius1" in meshInfo) { // Cylinder
                geometry = new THREE.CylinderGeometry(meshInfo.Radius1, meshInfo.Radius2, meshInfo.Height);
            } else if ("LatitudeFrom" in meshInfo) { // Sphere
                geometry = new THREE.SphereGeometry(meshInfo.Radius, 8, 6, meshInfo.LatitudeStart, meshInfo.LatitudeLength, meshInfo.LongitudeStart, meshInfo.LongitudeLength);
            } else if ("Text" in meshInfo) { // Text
                geometry = new THREE.TextGeometry(meshInfo.Text != null ? meshInfo.Text : "", {
                    height: 0.001,
                    size: 0.6,
                    curveSegments: 4,
                    font: "optimer", // helvetiker, optimer, gentilis
                    weight: "normal", // normal bold
                    style: "normal" // normal italic
                });
            } else if ("Width" in meshInfo) { // Plane Surface
                geometry = new THREE.BoxGeometry(meshInfo.Width, 0.01, meshInfo.Length);
            } else if ("Points" in meshInfo) { // Mesh or PointCloud
                geometry = new THREE.Geometry();

                var points = meshInfo.Points.split(",");
                if ("Faces" in meshInfo) { // Mesh
                    for (var pi = 0, lenPoints = points.length; pi < lenPoints; pi += 3) {
                        geometry.vertices.push(new THREE.Vector3(
                            parseFloat(points[pi]),
                            parseFloat( points[pi + 1]),
                            parseFloat(points[pi + 2])
                        ));
                    }

                    var faces = meshInfo.Faces.split(",");
                    for (var fi = 0, lenFaces = faces.length; fi < lenFaces; fi += 3) {
                        geometry.faces.push(new THREE.Face3(
                            parseFloat(faces[fi]),
                            parseFloat(faces[fi + 1]),
                            parseFloat(faces[fi + 2])
                        ));
                    }

                    geometry.computeFaceNormals();

                    wireframeMaterial = new THREE.MeshLambertMaterial({ color: meshInfo.Color, opacity: meshInfo.Opacity, transparent: meshInfo.Opacity < 1, side: THREE.DoubleSide, wireframe: true });
                } else { // PointCloud
                    const pointCloud = meshInfo as MeshPointCloud;

                    let vertexFilter: (meshInfo: MeshPointCloud, pointIdx: number) => boolean = "VertexFilter" in pointCloud ? pointCloud["VertexFilter"] : (meshInfo, pointIdx) => true;
                    let colorStrategy: (meshInfo: MeshPointCloud, pointIdx: number) => number = "ColorStrategy" in pointCloud ? pointCloud["ColorStrategy"] : (meshInfo, pointIdx) => meshInfo.Color;

                    for (var pi = 0, lenPoints = points.length, pointIdx = 0; pi < lenPoints; pi += 3, pointIdx++) {
                        if (vertexFilter(pointCloud, pointIdx)) {
                            geometry.vertices.push(new THREE.Vector3(
                                parseFloat(points[pi]),
                                parseFloat(points[pi + 1]),
                                parseFloat(points[pi + 2])
                            ));
                            geometry.colors.push(new THREE.Color(colorStrategy(pointCloud, pointIdx)));
                        }
                    }

                    surfaceMaterial = undefined;
                    defaultMaterial = pointCloudMaterial; // its a point cloud.. surface material would be pointless (Pun intended :) )!
                }
            } else {
                return undefined;
            }

            mesh = surfaceMaterial ? new THREE.Mesh(geometry, defaultMaterial) : new THREE.PointCloud(geometry, pointCloudMaterial);

            mesh.SurfaceMaterial = surfaceMaterial;
            mesh.WireframeMaterial = wireframeMaterial;
            mesh.PointCloudMaterial = pointCloudMaterial;
        }

        var meshTrans = meshInfo.Transform;
        if (meshTrans) {
            mesh.position.x = meshTrans.PositionX;
            mesh.position.y = meshTrans.PositionY;
            mesh.position.z = meshTrans.PositionZ;

            mesh.rotation.x = meshTrans.AngleX;
            mesh.rotation.y = meshTrans.AngleY;
            mesh.rotation.z = meshTrans.AngleZ;

            mesh.scale.x = meshTrans.ScaleX;
            mesh.scale.y = meshTrans.ScaleY;
            mesh.scale.z = meshTrans.ScaleZ;
        }

        meshInfo.mesh = mesh;
        mesh.meshInfo = meshInfo; // cyclic link.. do we really need that!?
        mesh.visible = meshInfo.IsVisible;

        return mesh;
    }

    private _changeRendering(object3D, newRenderingMode: SurfaceMaterial): TemsObject3D {
        if (typeof object3D !== "undefined") {
            if (object3D.type === 'Group') { // THREE.Group.type) {
                object3D.children = object3D.children.map((child) => this._changeRendering(child, newRenderingMode));
            } else {
                // If Mesh
                if (object3D.meshInfo && object3D.meshInfo.Points && object3D.SurfaceMaterial) {
                    object3D.visible = (newRenderingMode !== SurfaceMaterial.Points);
                } 

                this.setObjectMaterial(object3D, newRenderingMode);
            }
            this.fireMeshChange();
        }

        return object3D;
    }

    getObject3DFromMeshId(meshId: string, parent: any = this.sceneryObject): TemsObject3D {

        if (typeof (parent) === "undefined") {
            return null;
        }

        for (var i = 0; i < parent.children.length; i++) {
            if ("meshInfo" in parent.children[i]) {
                if (parent.children[i].meshInfo.Id === meshId) {
                    return parent.children[i];
                }
            }

            var foundInChild = this.getObject3DFromMeshId(meshId, parent.children[i]);
            if (foundInChild) {
                return foundInChild;
            }
        }

        return null;
    }

    public applyTextures(meshId: string, thermoInfos: ResultThermoTextureInfo[]): void {
        var object3D = this.getObject3DFromMeshId(meshId);
        if (!object3D) {
            console.error("Can't apply texture, didn't find mesh: " + meshId);
            return;
        }

        var meshInfo = object3D.meshInfo;
        var geometry = object3D.geometry;
        var face;

        //  create materials
        var materials = [];

        // create an 'empty' material as well
        materials.push(new THREE.MeshPhongMaterial({
            color: meshInfo.Color,
            opacity: meshInfo.Opacity,
            transparent: meshInfo.Opacity < 1,
            side: THREE.DoubleSide,
            shading: THREE.FlatShading
        }));

        // clear all material indices
        for (var fi = 0, lenFaces2 = geometry.faces.length; fi < lenFaces2; fi++) {
            geometry.faces[fi].materialIndex = 0;
        }

        for (var ti = 0, lenThermoInfo = thermoInfos.length; ti < lenThermoInfo; ti++) {
            var texture = THREE.ImageUtils.loadTexture("data:image/png;base64," + thermoInfos[ti].Image, THREE.UVMapping, tex => {
                tex.minFilter = THREE.NearestFilter;
                tex.needsUpdate = true;
            });

            var material = new THREE.MeshPhongMaterial({
                color: meshInfo.Color,
                opacity: meshInfo.Opacity,
                transparent: meshInfo.Opacity < 1,
                side: THREE.DoubleSide,
                map: texture,
                shading: THREE.FlatShading
            });

            materials.push(material);

            // assign the MaterialIndex, based on the TextureInfo, only ThermoTextureInfo supported
            var materialIdx = materials.length - 1;
            if ("Side" in thermoInfos[ti]) {
                switch (thermoInfos[ti].Side) {
                    default:
                    case ResultPartSide.None:
                        this.applyMaterial(geometry.faces, geometry.vertices, materialIdx, () => true);
                        break;
                    case ResultPartSide.Right:
                        this.applyMaterial(geometry.faces, geometry.vertices, materialIdx, vertex => (vertex.x <= 0.0));
                        break;
                    case ResultPartSide.Left:
                        this.applyMaterial(geometry.faces, geometry.vertices, materialIdx, vertex => (vertex.x > 0.0));
                        break;
                }
            }
        }

        // compute UV
        var minZ = 0.0;
        for (var ii = 0, lenVertices = geometry.vertices.length; ii < lenVertices; ii++) {
            minZ = Math.min(geometry.vertices[ii].z, minZ);
        }

        var uvs = [];
        for (var iii = 0, lenFaces = geometry.faces.length; iii < lenFaces; iii++) {
            face = geometry.faces[iii];
            var v1 = geometry.vertices[face.a];
            var v2 = geometry.vertices[face.b];
            var v3 = geometry.vertices[face.c];

            uvs.push([
                this.uvFromVertex(v1, -minZ, thermoInfos[face.materialIndex - 1]),  // -1 because we have the 'no texture material' as materialIndex=0
                this.uvFromVertex(v2, -minZ, thermoInfos[face.materialIndex - 1]),
                this.uvFromVertex(v3, -minZ, thermoInfos[face.materialIndex - 1])
            ]);
        }

        // assign and request buffer updates
        var isCurrentlySurfaceRendering = object3D.material === object3D.SurfaceMaterial;

        // destory the old default material first
        if (object3D.SurfaceMaterial) {
            this.removeMaterial(object3D.SurfaceMaterial);
        }

        object3D.SurfaceMaterial = new THREE.MeshFaceMaterial(materials);
        geometry.faceVertexUvs[0] = uvs;
        geometry.uvsNeedUpdate = true;
        geometry.elementsNeedUpdate = true;

        if (isCurrentlySurfaceRendering) {
            object3D.material = object3D.SurfaceMaterial;
            // just 'material.needsUpdate' doesn't work... 
            this.exchangeObject(object3D, new THREE.Mesh(object3D.geometry.clone(), object3D.material));
        }

        this.fireMeshChange();
    }

    public setPartVisibility(partId: string, visible: boolean): void {
        var object3D = this.getObject3DFromMeshId(partId);
        if (object3D) {
            object3D.visible = visible;
            object3D.meshInfo.IsVisible = visible;

            this.fireMeshChange();
        }
    }

    exchangeObject(oldObject3D, newObject3D, useNewTransform: boolean = false) {
        if (typeof (oldObject3D) === "undefined") {
            this.sceneryObject.add(newObject3D);
            return newObject3D;
        }

        var meshInfo = oldObject3D.meshInfo; // keep this thing around, they contain the data that was initially requested from the server and generated later
        var parent = oldObject3D.parent;
        parent.remove(oldObject3D);

        if (newObject3D) {
            newObject3D.meshInfo = meshInfo;
            newObject3D.visible = meshInfo.IsVisible;
            if (!useNewTransform) {
                newObject3D.applyMatrix(oldObject3D.matrix);
            }

            newObject3D.SurfaceMaterial = oldObject3D.SurfaceMaterial;
            newObject3D.PointCloudMaterial = oldObject3D.PointCloudMaterial;
            newObject3D.WireframeMaterial = oldObject3D.WireframeMaterial;

            parent.add(newObject3D);
            meshInfo.mesh = newObject3D;
        }

        this.fireMeshChange();

        return newObject3D;
    }

    public removeMeshById(meshId: string): void {
        var oldObject3D = this.getObject3DFromMeshId(meshId);
        if (oldObject3D) {
            this.removeObject(oldObject3D);
        }

        this.fireMeshChange();
    }

    public getMeshes(predicate, root: THREE.Object3D = this.sceneryObject): TemsObject3D[] {
        var res = [];
        if (root) {
            for (var i = 0; i < root.children.length; i++) {
                if ("meshInfo" in root.children[i]) {
                    if (predicate(root.children[i])) {
                        res.push(root.children[i]);
                    }
                }

                res = res.concat(this.getMeshes(predicate, root.children[i]));
            }
        }

        return res;
    }

    public removeObject(obj: any): void {
        if (!obj) {
            return;
        }

        // recursive traversal
        if (obj.children) {
            for (let i = obj.children.length - 1; i >= 0; i--) {
                this.removeObject(obj.children[i]);
            }
        }

        // Detach from parent
        if (obj.parent) {
            obj.parent.remove(obj);
        }

        // Destroy geometry
        if (obj.geometry) {
            obj.geometry.dispose();
        }

        // Destroy material
        if (obj.material) {
            // one of our meshes, we save additional materials in the meshInfo to switch them on the fly
            if (obj.meshInfo) {
                if (obj.WireframeMaterial && obj.WireframeMaterial !== obj.material) {
                    this.removeMaterial(obj.WireframeMaterial);
                }
                if (obj.PointCloudMaterial && obj.PointCloudMaterial !== obj.material) {
                    this.removeMaterial(obj.PointCloudMaterial);
                }
                if (obj.SurfaceMaterial && obj.SurfaceMaterial !== obj.material) {
                    this.removeMaterial(obj.SurfaceMaterial);
                }
            }

            this.removeMaterial(obj.material);

        }

        if (obj.dispose) {
            obj.dispose();
        }
    }

    public updateMesh(newMeshInfo: MeshInfo, useNewTransform: boolean = false): void {
        const oldObject3D = this.getObject3DFromMeshId(newMeshInfo.Id);
        if (oldObject3D == null) {
            return;
        }

        const meshObject = this.getMeshFromMeshInfo(newMeshInfo);
        this.exchangeObject(oldObject3D, meshObject, useNewTransform);
    }

    public addDummyElement(meshId: string, xOffset: number = 0, reverse: boolean = false, parent: THREE.Object3D = this.sceneryObject): TemsObject3D {
        const dummyMeshObject = this.getMeshFromMeshInfo({
            "Id": meshId,
            "Width": 0.001,
            "Length": 0.001,
            "IsVisible": true,
            "Opacity": 1,
            "Color": 12345,
            "Transform": {
                "PositionX": xOffset,
                "PositionY": 0,
                "PositionZ": 0,
                "AngleX": 0,
                "AngleY": reverse ? Math.PI : 0,
                "AngleZ": 0,
                "ScaleX": 1,
                "ScaleY": 1,
                "ScaleZ": 1
            }
        });

        parent.add(dummyMeshObject);
        return dummyMeshObject;
    }

    removeMaterial(mat): void {
        if (mat instanceof THREE.MeshFaceMaterial) {
            $.each(mat.materials, (idx, mat) => {
                if ((<any>mat).map && (<any>mat).map.dispose) {
                    (<any>mat).map.dispose(); // Texture
                }

                mat.dispose();
            });
        } else {
            if (mat.map && mat.map.dispose && mat.map !== pointSprite) { // don't destroy this very special material
                mat.map.dispose(); // Texture
            }

            mat.dispose();
        }
    }

    uvFromVertex(vertex, shiftZ: number, thermoInfo: ResultThermoTextureInfo): THREE.Vector2 {
        // doesn't account for Top/Bottom/Front/Back/None  side
        if (typeof thermoInfo === "undefined") {
            return new THREE.Vector2(0, 0);
        }

        var z = Number(vertex.z);
        var y = Number(vertex.y);

        var borderFrontMm = thermoInfo.BorderFrontMm;
        var borderBottomMm = thermoInfo.BorderBottomMm;
        var mmPerPixel = thermoInfo.MmPerPixel;
        var width = thermoInfo.Width;
        var height = thermoInfo.Height;

        var u = (((z + shiftZ) * 1000 + borderFrontMm) / mmPerPixel) / width;
        var v = ((y * 1000 + borderBottomMm) / mmPerPixel) / height;
        return new THREE.Vector2(u, v);
    }

    setObjectMaterial(object3D: TemsObject3D, newRenderingMode: SurfaceMaterial): void {
        if (newRenderingMode === SurfaceMaterial.Points && object3D.PointCloudMaterial) {
            object3D.material = object3D.PointCloudMaterial;
        } else if (newRenderingMode === SurfaceMaterial.Wireframe && object3D.WireframeMaterial) {
            object3D.material = object3D.WireframeMaterial;
        } else if (object3D.SurfaceMaterial) {
            object3D.material = object3D.SurfaceMaterial;
        }

        this.fireMeshChange();

        object3D.material.needsUpdate = true;
    }

    applyMaterial(faces, vertices, materialIndex: number, vertexEvaluator): void {
        for (var i = 0, len = faces.length; i < len; i++) {
            var face = faces[i];
            if (face.materialIndex === 0 && vertexEvaluator(vertices[face.a])) {
                face.materialIndex = materialIndex;
            }
        }
    }

    private shaders = {
        "VerticalCandyStripesAlongY": {
            // http://threejs.org/docs/#Reference/Materials/ShaderMaterial 
            // not defining any arguments gives us: uv and position
            vertexShader:
            "varying vec3 worldPos; " +
            "void main() { " +
            "   worldPos = position.xyz; " +
            "   gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 ); " +
            "}",
            fragmentShader:
            "varying vec3 worldPos; " +
            "void main() { " +
            "   bool uWhite = mod(worldPos.y-0.375, 1.5) - 0.75 <= 0.0;" + // why y? because this was first used on a cylinder that extruded along y
            "   if (uWhite) {" +
            "       gl_FragColor = vec4(0.95,0.95,0.9,1);" +
            "   } else {" +
            "       gl_FragColor = vec4(1,0.1,0.1,1);" +
            "   }" +
            "}"
        }
    };   
}